import { useEffect, useState } from 'react';

// Reads approval_status from localStorage 'user' JSON (populated by login/
// register/me endpoints). Auto-refreshes from /api/users/me at most once
// per 30s across the whole app (module-level dedupe) so a page full of
// ProductCards doesn't fan out into 24 parallel requests.

function readUser() {
  try { return JSON.parse(localStorage.getItem('user') || 'null'); }
  catch (_) { return null; }
}

let lastFetchAt = 0;
let inFlight = null;
const subscribers = new Set();

async function refreshFromServer(force = false) {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  if (!token) return;
  const now = Date.now();
  if (!force && now - lastFetchAt < 30_000) return;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const r = await fetch('/api/users/me', { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return;
      const fresh = await r.json();
      const cached = readUser();
      if (!cached || cached.approval_status !== fresh.approval_status || cached.rejection_reason !== fresh.rejection_reason) {
        const merged = { ...(cached || {}), ...fresh };
        localStorage.setItem('user', JSON.stringify(merged));
        subscribers.forEach(fn => fn(merged));
      }
    } catch (_) {} finally {
      lastFetchAt = Date.now();
      inFlight = null;
    }
  })();
  return inFlight;
}

export default function useApprovalStatus() {
  const [user, setUser] = useState(readUser());

  useEffect(() => {
    subscribers.add(setUser);
    refreshFromServer();

    const onStorage = (e) => {
      if (e.key === 'user' || e.key === 'token' || e.key === null) setUser(readUser());
    };
    const onFocus = () => { setUser(readUser()); refreshFromServer(); };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    return () => {
      subscribers.delete(setUser);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const isLoggedIn = !!localStorage.getItem('token') || !!localStorage.getItem('authToken');
  const status = user?.approval_status || (isLoggedIn ? 'approved' : null);
  const isApproved = isLoggedIn && status === 'approved';
  return { isLoggedIn, isApproved, status, reason: user?.rejection_reason || null };
}
